// FireChecksTab — "Fire checks" tab on the Fire Safety page (Prompt 6).
// Lists fire_check-category form instances (Aspire-style consolidated weekly
// checks) for the selected client/building, lets internal + portal-edit roles
// start a new Draft against the generic form engine, and downloads the
// (co-branded) PDF once submitted/approved.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlameKindling, Plus, Loader2, FileDown, X, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, filenameFromResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { enabledPortalRouteSet } from "@/lib/portalModules";
import Pill from "@/components/Pill";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";

const STATUS_TONE = { Draft: "amber", Submitted: "blue", Approved: "green", Rejected: "red" };
const CAN_EDIT = new Set(["director", "administrator", "consultant", "client_admin", "site_manager"]);
const CAN_DELETE = new Set(["director", "administrator"]);

// Monthly fire check templates (PL-ASPIRE-MFC-N1..N4 / S1/S2) are each tied to
// one building side by their code suffix — used to gate "pick a building
// first, then only see that building's templates" in the New dialog below.
// Any other category (e.g. the single combined weekly fire check) has no
// templates matching this pattern and simply falls back to the old behaviour.
function templateBuildingSide(t) {
    const m = /-(N|S)\d+$/.exec(t.code || "");
    return m ? m[1] : null;
}
function buildingSide(b) {
    const n = (b?.name || "").toLowerCase();
    if (n.includes("north")) return "N";
    if (n.includes("south")) return "S";
    return null;
}

// Which client(s) are even eligible to show this tab — have >=1 published
// fire_check (or fire_check_monthly) template AND haven't had the "fire"
// portal module switched off.
export function useFireCheckEligibleClients(category = "fire_check") {
    const [eligible, setEligible] = useState(null); // null = loading
    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/forms/templates/fire-check-clients", { params: { category } }),
            api.get("/clients"),
        ]).then(([fc, cl]) => {
            if (!alive) return;
            const ids = new Set(fc.data || []);
            const clients = (cl.data || []).filter((c) => {
                if (!ids.has(c.id)) return false;
                const set = enabledPortalRouteSet(c);
                return !set || set.has("fire");
            });
            setEligible(clients);
        }).catch(() => { if (alive) setEligible([]); });
        return () => { alive = false; };
    }, [category]);
    return eligible; // null while loading, else a (possibly empty) client array
}

export default function FireChecksTab({ eligibleClients: eligibleClientsProp, category = "fire_check",
                                       description = "Consolidated weekly fire checks (e.g. the Aspire North / South master check) — one digital form replacing the paper sheets, with the right sections for the chosen building and week.",
                                       newButtonLabel = "New fire check", emptyLabel = "fire checks" }) {
    // Defensive against a direct deep-link (e.g. ?tab=monthlyfirechecks, used by
    // the "back" link from an inspection) landing before the parent's async
    // eligibility fetch resolves — it's null while loading.
    const eligibleClients = eligibleClientsProp || [];
    const { user } = useAuth();
    const navigate = useNavigate();
    const backTab = category === "fire_check_monthly" ? "monthlyfirechecks" : "firechecks";
    const backTo = `/app/fire?tab=${backTab}`;
    const { portalClientId, isInternalView } = useView();
    const lockedClient = portalClientId && !isInternalView;
    const canEdit = user && CAN_EDIT.has(user.role);
    const canDelete = user && CAN_DELETE.has(user.role);
    // Mirrors InspectionsPage: a PR-completed template (Aspire is completed_by="pr")
    // stays start-only for internal staff — client_admin/site_manager get a
    // read-only register here until/unless a template is explicitly handed to
    // them as completed_by="client".
    const isPortalRole = user?.role === "client_admin" || user?.role === "site_manager";

    const [clientId, setClientId] = useState(lockedClient ? portalClientId : (eligibleClients[0]?.id || ""));
    const [buildings, setBuildings] = useState([]);
    const [buildingId, setBuildingId] = useState("");
    const [templates, setTemplates] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [pdfBusy, setPdfBusy] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    useEffect(() => { if (lockedClient) setClientId(portalClientId); }, [lockedClient, portalClientId]);
    // Deep-link race (e.g. ?tab=monthlyfirechecks): eligibleClients can resolve
    // from [] to a populated array a tick after mount — sync clientId once it does.
    useEffect(() => {
        if (!lockedClient && !clientId && eligibleClients.length) setClientId(eligibleClients[0].id);
    }, [eligibleClients, lockedClient, clientId]);

    useEffect(() => {
        if (!clientId) return;
        api.get("/buildings", { params: { client_id: clientId } }).then((r) => setBuildings(r.data || [])).catch(() => setBuildings([]));
        api.get("/forms/templates", { params: { category, status: "published", client_id: clientId } })
            .then((r) => setTemplates(r.data || [])).catch(() => setTemplates([]));
    }, [clientId, category]);

    const dialogTemplates = isPortalRole ? templates.filter((t) => t.completed_by === "client") : templates;

    const refresh = useCallback(() => {
        if (!clientId) { setRows([]); setLoading(false); return; }
        setLoading(true);
        const params = { category, client_id: clientId };
        if (buildingId) params.building_id = buildingId;
        api.get("/forms/instances", { params }).then((r) => setRows(r.data || []))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load fire checks"))
            .finally(() => setLoading(false));
    }, [clientId, buildingId, category]);
    useEffect(refresh, [refresh]);

    const buildingMap = useMemo(() => Object.fromEntries(buildings.map((b) => [b.id, b])), [buildings]);

    async function downloadPdf(row) {
        setPdfBusy(row.id);
        try {
            const res = await api.get(`/forms/instances/${row.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filenameFromResponse(res, `${row.title || "fire-check"}.pdf`);
            a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Could not generate PDF"); }
        setPdfBusy(null);
    }

    async function deleteRow(row) {
        setDeleting(row.id);
        try {
            await api.delete(`/forms/instances/${row.id}`);
            toast.success("Fire check deleted");
            setConfirmDelete(null);
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not delete"); }
        setDeleting(null);
    }

    return (
        <div className="space-y-4" data-testid="firechecks-tab">
            <p className="text-[12.5px]" style={{ color: "#6F6A64" }}>
                {description}
            </p>
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div className="flex items-end gap-2 flex-wrap">
                    {!lockedClient && (
                        <select data-testid="firechecks-client-filter" value={clientId}
                            onChange={(e) => { setClientId(e.target.value); setBuildingId(""); }}
                            className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            {eligibleClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                    <select data-testid="firechecks-building-filter" value={buildingId} onChange={(e) => setBuildingId(e.target.value)}
                        className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                        <option value="">All buildings</option>
                        {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                </div>
                {canEdit && dialogTemplates.length > 0 && (
                    <button data-testid="firechecks-new-button" onClick={() => setDialog(true)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus className="w-3.5 h-3.5" /> {newButtonLabel}
                    </button>
                )}
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-[12.7px]">
                    <thead className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                        <tr>
                            <th className="py-2.5 px-4 text-left">Fire check</th>
                            <th className="py-2.5 px-4 text-left">Building</th>
                            <th className="py-2.5 px-4 text-left">Due / created</th>
                            <th className="py-2.5 px-4 text-left">Status</th>
                            <th className="py-2.5 px-4 text-right">PDF</th>
                            {canDelete && <th className="py-2.5 px-4 text-right">Delete</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={canDelete ? 6 : 5} className="text-center py-10" style={{ color: "#9A968F" }}><Loader2 className="inline animate-spin mr-2 w-4 h-4" />Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={canDelete ? 6 : 5} className="text-center py-10 italic" style={{ color: "#9A968F" }}>No {emptyLabel} recorded yet.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id} data-testid={`firecheck-row-${r.id}`} onClick={() => navigate(`/app/inspections/${r.id}?back=${encodeURIComponent(backTo)}`)}
                                className="border-t hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-[#1F1F1F]">{r.title || r.template_name}</div>
                                    <div className="text-[11px] font-mono" style={{ color: "#9A968F" }}>{r.template_code}</div>
                                </td>
                                <td className="px-4 py-3" style={{ color: "#6F6A64" }}>{buildingMap[r.building_id]?.name || "—"}</td>
                                <td className="px-4 py-3 font-mono text-[11.5px]" style={{ color: "#6F6A64" }}>{r.due_date || (r.created_at || "").slice(0, 10)}</td>
                                <td className="px-4 py-3"><Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill></td>
                                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                    {["Submitted", "Approved"].includes(r.status) && (
                                        <button data-testid={`firecheck-pdf-${r.id}`} onClick={() => downloadPdf(r)} disabled={pdfBusy === r.id}
                                            className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-[7px] border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                                            {pdfBusy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
                                        </button>
                                    )}
                                </td>
                                {canDelete && (
                                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                        <button data-testid={`firecheck-delete-${r.id}`} onClick={() => setConfirmDelete(r)} aria-label="Delete fire check"
                                            className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-[7px] border bg-white hover:bg-[#FBE8E5] hover:border-[#E8B4AA]" style={{ borderColor: "#E5E0DA", color: "#B33A2E" }}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {dialog && (
                <NewFireCheckDialog clientId={clientId} buildings={buildings} templates={dialogTemplates} title={newButtonLabel}
                    defaultBuildingId={buildingId}
                    onClose={() => setDialog(false)}
                    onCreated={(inst) => { setDialog(false); navigate(`/app/inspections/${inst.id}?back=${encodeURIComponent(backTo)}`); }} />
            )}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="firecheck-delete-confirm-dialog">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-3">
                        <h3 className="text-base font-bold text-[#1F1F1F]">Delete this fire check?</h3>
                        <p className="text-[12.5px]" style={{ color: "#6F6A64" }}>
                            "{confirmDelete.title || confirmDelete.template_name}" ({confirmDelete.status}) will be removed from this list. This can be recovered by a PR administrator if needed.
                        </p>
                        <div className="flex items-center justify-end gap-2 pt-2">
                            <button data-testid="firecheck-delete-cancel" onClick={() => setConfirmDelete(null)}
                                className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                            <button data-testid="firecheck-delete-confirm" onClick={() => deleteRow(confirmDelete)} disabled={deleting === confirmDelete.id}
                                className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white bg-[#B33A2E] hover:bg-[#9B3126] disabled:opacity-60">
                                {deleting === confirmDelete.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function NewFireCheckDialog({ clientId, buildings, templates, onClose, onCreated, title = "New fire check", defaultBuildingId = "" }) {
    // If any of the offered templates carry a North/South code suffix, this is
    // a "pick the building first" flow (Monthly Fire Checks) — otherwise fall
    // back to the original behaviour (Building stays an optional field).
    const buildingGated = useMemo(() => templates.some((t) => templateBuildingSide(t)), [templates]);
    const [buildingId, setBuildingId] = useState(defaultBuildingId || "");
    const buildingMap = useMemo(() => Object.fromEntries(buildings.map((b) => [b.id, b])), [buildings]);
    const templateOptions = useMemo(() => {
        if (!buildingGated) return templates;
        if (!buildingId) return [];
        const side = buildingSide(buildingMap[buildingId]);
        return templates.filter((t) => !side || templateBuildingSide(t) === side);
    }, [buildingGated, buildingId, buildingMap, templates]);
    const [templateId, setTemplateId] = useState(() => (buildingGated ? "" : (templates[0]?.id || "")));
    const [dueDate, setDueDate] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!templateOptions.some((t) => t.id === templateId)) {
            setTemplateId(templateOptions.length === 1 ? templateOptions[0].id : "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateOptions]);

    async function submit() {
        if (buildingGated && !buildingId) { toast.error("Select a building first"); return; }
        if (!templateId) { toast.error("Select a fire check template"); return; }
        setSaving(true);
        try {
            const payload = { template_id: templateId, client_id: clientId };
            if (buildingId) payload.building_id = buildingId;
            if (dueDate) payload.due_date = dueDate;
            const { data } = await api.post("/forms/instances", payload);
            toast.success("Fire check created");
            onCreated(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create"); }
        setSaving(false);
    }

    const TemplatePicker = () => (
        <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1 block">Template</span>
            <Select value={templateId} onValueChange={setTemplateId} disabled={!templateOptions.length}>
                <SelectTrigger data-testid="new-firecheck-template" className="w-full h-9 text-[12.5px]">
                    <SelectValue placeholder={buildingGated && !buildingId ? "Select a building first" : "Select a template"} />
                </SelectTrigger>
                <SelectContent className="max-w-[min(92vw,460px)]">
                    {templateOptions.map((t) => (
                        <SelectItem key={t.id} value={t.id} className="whitespace-normal py-2 pr-7 text-[12.5px] leading-snug">
                            {t.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </label>
    );

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="new-firecheck-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F] flex items-center gap-2"><FlameKindling className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} /> {title}</h3>
                    <button data-testid="new-firecheck-close" onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3 flex-1 overflow-y-auto">
                    {buildingGated ? (
                        <>
                            <label className="block">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1 block">Building<span className="text-red-600 ml-0.5">*</span></span>
                                <select data-testid="new-firecheck-building" value={buildingId}
                                    onChange={(e) => setBuildingId(e.target.value)}
                                    className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                                    <option value="">— Select a building —</option>
                                    {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                            </label>
                            <TemplatePicker />
                        </>
                    ) : (
                        <>
                            {templates.length > 1 && <TemplatePicker />}
                            <label className="block">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1 block">Building (optional — can be set inside the form)</span>
                                <select data-testid="new-firecheck-building" value={buildingId} onChange={(e) => setBuildingId(e.target.value)}
                                    className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                                    <option value="">— None —</option>
                                    {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                            </label>
                        </>
                    )}
                    <label className="block">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1 block">Due date (optional)</span>
                        <input data-testid="new-firecheck-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                            className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm" />
                    </label>
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="new-firecheck-save" onClick={submit} disabled={saving}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create
                    </button>
                </div>
            </div>
        </div>
    );
}
